class Accordion {
  #eventDetail;
  #events;

  /**
   * @param {HTMLElement} domNode - The DOM element to initialise on
   * @param {AccordionDefault} opts - Optional params to modify functionality
   */
  constructor(domNode, opts) {
    /** @type {AccordionDefault}*/
    const DEFAULTS = {
      onBeforeOpen: () => {},
      onAfterOpen: () => {},
      onBeforeClose: () => {},
      onAfterClose: () => {},
      itemSelector: '[f11y-accordion-item]',
      showMultiple: true
    };

    /** @type {AccordionDefault}*/
    this.options = Object.assign(DEFAULTS, opts);

    /** @type {HTMLElement} The passed domNode */
    this.domNode = domNode;
    this.#eventDetail = {
      detail: {
        component: this
      }
    };
    this.#events = {
      initialised: new CustomEvent('f11yinit', this.#eventDetail),
      initialisedItem: new CustomEvent('f11yinititem', this.#eventDetail),
      destroyed: new CustomEvent('f11ydestroyed', this.#eventDetail),
      beforeOpen: new CustomEvent('f11ybeforeopen', this.#eventDetail),
      afterOpen: new CustomEvent('f11yafteropen', this.#eventDetail),
      beforeClose: new CustomEvent('f11ybeforeclose', this.#eventDetail),
      afterClose: new CustomEvent('f11yafterclose', this.#eventDetail)
    };
    this.init();
  }

  /**
   * Initialises the class component
   */
  init() {
    /** @type {AccordionItemObject[]} */
    this.items = [];

    /** @type {Function[]} */
    this.toggleHandlers = [];

    /** @type {HTMLElement[]} */
    const items = Array.from(this.domNode.querySelectorAll(this.options.itemSelector));
    for (let i = 0; i < items.length; i += 1) {
      /** @type {HTMLElement} */
      const item = items[i];
      this.initItem(item);
    }
    this.domNode.classList.add(window.f11y.settings.initialisedClass);
    window.dispatchEvent(this.#events.initialised);
    this.domNode.dispatchEvent(this.#events.initialised);
  }

  /**
   * 
   * @param {HTMLElement} item 
   * @returns {AccordionItemObject|undefined}
   */
  initItem(item) {
    /** @type {HTMLElement|null} */
    const trigger = item.querySelector("[aria-controls]");
    if (!trigger) {
      console.warn(`Trigger is not defined for item at index:`, item);
      return;
    }

    /** @type {string|null} */
    const ariaControls = trigger.getAttribute('aria-controls');
    if (!ariaControls) {
      console.warn(`aria-controls attribute is null for item at index:`, item);
      return;
    }

    /** @type {HTMLElement|null} */
    const panel = document.getElementById(ariaControls);
    if (!panel) {
      console.warn(`Panel is not defined for item at index:`, item);
      return;
    }

    /** @type {string|null} */
    const ariaExpanded = trigger.getAttribute('aria-expanded');
    if (!ariaExpanded) {
      console.warn(`aria-expanded attribute is not set for:`, item, trigger);
      return;
    }

    /** @type {AccordionItemObject} Stores item information */
    const itemObj = {
      item: item,
      panel: panel,
      trigger: trigger,
      isOpen: ariaExpanded
    };
    itemObj.isOpen === "true" ? panel.removeAttribute('hidden') : panel.setAttribute('hidden', '');
    this.items.push(itemObj);

    /**
     * Handler for toggle event.
     * @param {Event} event
     */
    const toggleHandler = event => this.toggle(itemObj, event);
    trigger.addEventListener("click", toggleHandler);
    this.toggleHandlers.push(toggleHandler);
    item.dispatchEvent(this.#events.initialisedItem);
    return itemObj;
  }

  /**
   * Removes event listeners, clears member arrays and resets DOM changes e.g. class names
   */
  destroy() {
    this.domNode.classList.remove(window.f11y.settings.initialisedClass);
    if (!this.toggleHandlers || !this.items) return;
    for (let i = 0; i < this.toggleHandlers.length; i++) {
      const trigger = this.items[i].trigger;
      const toggleHandler = this.toggleHandlers[i];
      trigger.removeEventListener("click", toggleHandler);
    }
    this.items = [];
    this.toggleHandlers = [];
    window.dispatchEvent(this.#events.destroyed);
    this.domNode.dispatchEvent(this.#events.destroyed);
  }
  dispatchStateUpdateEvent() {
    window.dispatchEvent(window.f11y.events.stateUpdate);
  }

  /**
   * Toggles passed accordion item.
   * @param  {AccordionItemObject} item Object that represents a singular accordion item.
   * @param  {Event|KeyboardEvent|null} event The event that triggered this function method.
   */
  toggle(item) {
    let event = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    const itemState = item.isOpen;
    if (this.options.showMultiple === false) this.closeAll(event);
    if (itemState === "false") {
      this.openItem(item, event);
      return;
    }
    if (itemState === "true") {
      this.closeItem(item, event);
      return;
    }
  }

  /**
   * Closes all accordion items.
   * @param  {Event|KeyboardEvent|null} event The event that triggered this function method.
   */
  closeAll() {
    let event = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    if (!this.items) return;
    for (let i = 0; i < this.items.length; i += 1) {
      if (this.items[i].isOpen == "true") {
        this.closeItem(this.items[i], event);
      }
    }
  }

  /**
   * Opens all accordion items.
   * @param  {Event|KeyboardEvent|null} event The event that triggered this function method.
   */
  openAll() {
    let event = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    if (this.options.showMultiple === false || !this.items) return;
    for (let i = 0; i < this.items.length; i += 1) {
      this.openItem(this.items[i], event);
    }
  }

  /**
   * Opens the passed accordion item.
   * @param  {AccordionItemObject} item Object that represents a singular accordion item.
   * @param  {Event|KeyboardEvent|null} event The event that triggered this function method.
   */
  openItem(item) {
    let event = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    if (item.isOpen === "true") return;
    this.domNode.dispatchEvent(this.#events.beforeOpen);
    this.options.onBeforeOpen(this, event, item);
    item.panel.removeAttribute("hidden");
    item.trigger.setAttribute("aria-expanded", "true");
    item.isOpen = "true";
    this.dispatchStateUpdateEvent();
    this.domNode.dispatchEvent(this.#events.afterOpen);
    this.options.onAfterOpen(this, event, item);
  }

  /**
   * Closes the passed accordion item.
   * @param  {AccordionItemObject} item Object that represents a singular accordion item.
   * @param  {Event|KeyboardEvent|null} event The event that triggered this function method.
   */
  closeItem(item) {
    let event = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    if (item.isOpen === "false" || !item) return;
    this.domNode.dispatchEvent(this.#events.beforeClose);
    this.options.onBeforeClose(this, event, item);
    item.panel.setAttribute("hidden", "");
    item.trigger.setAttribute("aria-expanded", "false");
    item.isOpen = "false";
    this.dispatchStateUpdateEvent();
    this.domNode.dispatchEvent(this.#events.afterClose);
    this.options.onAfterClose(this, event, item);
  }
}

/**
 * Checks if passed element is focusable (aka is within focusableElement array)
 * @param {HTMLElement} element 
 * @param {Array.<string>} filterSelectors 
 * @returns {boolean}
 */
function isFocusable(element) {
  let filterSelectors = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : window.f11y.focusableElements;
  const focusable = filterSelectors.join(', ');
  return element.matches(focusable);
}

/**
 * Gets Focusable elements within an element
 * @param {HTMLElement} element Check within this element
 * @returns {Node[]}
 */
function getFocusable(element) {
  function filter(node) {
    if (node.hasAttribute('hidden') || node.hasAttribute('aria-hidden') || getComputedStyle(node).display === 'none') {
      return NodeFilter.FILTER_REJECT;
    }
    if (isFocusable(node) === true) {
      return NodeFilter.FILTER_ACCEPT;
    }
    return NodeFilter.FILTER_SKIP;
  }
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_ELEMENT, {
    acceptNode: filter
  });
  let list = [];
  while (walker.nextNode()) {
    list.push(walker.currentNode);
  }
  return list;
}

class Dropdown {
  #eventDetail;
  #events;

  /**
   * @param  {HTMLElement|Node} domNode The DOM element to initialise on
   * @param  {DropdownDefault} opts Optional params to modify functionality
   */
  constructor(domNode, opts) {
    /** @type {DropdownDefault} */
    const DEFAULTS = {
      onBeforeOpen: () => {},
      onAfterOpen: () => {},
      onBeforeClose: () => {},
      onAfterClose: () => {},
      openClass: window.f11y.settings.openClass,
      focusableElements: window.f11y.focusableElements,
      updateOnSelect: false,
      updateSelector: null,
      closeOnBackgroundClick: true,
      openOnClick: true,
      openOnHover: false,
      focusOnMouseOver: true,
      closeOnSelect: false,
      arrowNavigation: true,
      tabNavigation: false,
      awaitOpenAnimation: false,
      awaitCloseAnimation: false
    };

    /** @type {DropdownDefault} */
    this.options = Object.assign(DEFAULTS, opts);

    /** @type {HTMLElement|Node} */
    this.domNode = domNode;
    this.#eventDetail = {
      detail: {
        component: this
      }
    };
    this.#events = {
      initialised: new CustomEvent('f11yinit', this.#eventDetail),
      initialisedItems: new CustomEvent('f11yinititems', this.#eventDetail),
      destroyed: new CustomEvent('f11ydestroyed', this.#eventDetail),
      beforeOpen: new CustomEvent('f11ybeforeopen', this.#eventDetail),
      afterOpen: new CustomEvent('f11yafteropen', this.#eventDetail),
      beforeClose: new CustomEvent('f11ybeforeclose', this.#eventDetail),
      afterClose: new CustomEvent('f11yafterclose', this.#eventDetail)
    };
    this.init();
  }
  init() {
    /** @type {HTMLElement[]} */
    this.dropdownItems = [];

    /** @type {String[]} */
    this.firstChars = [];

    /** @type {Function[]} */
    this.itemKeydownHandlers = [];

    /** @type {Function[]} */
    this.itemMouseoverHandlers = [];

    /** @type {Function[]} */
    this.itemClickHandlers = [];

    /** @type {HTMLElement|null} */
    this.trigger = this.domNode.querySelector('[aria-controls]');
    if (!this.trigger) return console.warn('Trigger element does not exist within: ', this.domNode);

    /** @type {string|null} */
    const ariaControls = this.trigger.getAttribute('aria-controls');
    if (!ariaControls) return console.warn(`aria-controls attribute is null for: `, this.trigger);

    /** @type {HTMLElement|null} */
    this.dropdown = document.getElementById(ariaControls);
    if (!this.dropdown) return console.warn('Dropdown element does not exist');
    if (this.options.updateSelector) this.updateTargetNode = document.querySelector(this.options.updateSelector);

    // Bind Node Listeners
    this.onTriggerKeydownBound = this.#onTriggerKeydown.bind(this);
    if (this.options.openOnClick === true) this.onTriggerClickBound = this.#onTriggerClick.bind(this);
    if (this.options.openOnHover === true) this.onTriggerHoverBound = this.#onTriggerHover.bind(this);
    this.onFocusInBound = this.#onFocusin.bind(this);
    this.onFocusOutBound = this.#onFocusout.bind(this);

    // Bind Window Listeners
    this.onBackgroundMousedownBound = this.#onBackgroundMousedown.bind(this);
    this.checkBoundingBoxBound = this.checkBoundingBox.bind(this);
    this.updateFocusableElmsBound = this.updateFocusableElms.bind(this);
    this.addNodeListeners();
    this.domNode.classList.add(window.f11y.settings.initialisedClass);
    window.dispatchEvent(this.#events.initialised);
    this.domNode.dispatchEvent(this.#events.initialised);
  }
  destroy() {
    window.dispatchEvent(this.#events.destroyed);
    this.domNode.dispatchEvent(this.#events.destroyed);
  }
  initDropdownItems() {
    if (!this.dropdown) return;

    /** @type {HTMLElement[]} */
    this.dropdownItems = [];

    /** @type {HTMLElement[]} */
    this.dropdownItems = getFocusable(this.dropdown);
    if (!this.dropdownItems) return;
    const itemKeydownHandler = this.#onItemKeydown.bind(this);
    const itemClickHandler = this.#onItemClick.bind(this);
    this.dropdownItems.forEach(item => {
      item.tabIndex = -1;
      if (item.innerText) {
        const firstChar = item.innerText.trim()[0].toLowerCase();
        if (firstChar && this.firstChars) this.firstChars.push(firstChar);
      }
      if (this.options.updateOnSelect === true) {
        item.addEventListener('click', itemClickHandler);
        this.itemClickHandlers.push(itemClickHandler);
      }
      item.addEventListener('keydown', itemKeydownHandler);
      this.itemKeydownHandlers.push(itemKeydownHandler);

      /** @type {Node|null} */
      if (!this.firstItem) this.firstItem = item;

      /** @type {Node|null} */
      this.lastItem = item;
    });
    this.domNode.dispatchEvent(this.#events.initialisedItems);
  }
  dispatchStateUpdateEvent() {
    window.dispatchEvent(window.f11y.events.stateUpdate);
  }

  /**
   * Adds bound method window events 
   */
  addGlobalListeners() {
    window.addEventListener('mousedown', this.onBackgroundMousedownBound);
    window.addEventListener('f11ystateupdate', this.updateFocusableElmsBound);
    document.addEventListener('scroll', this.checkBoundingBoxBound);
    window.addEventListener('resize', this.checkBoundingBoxBound);
  }

  /**
   * Removes bound methods window events 
   */
  removeGlobalListeners() {
    window.removeEventListener('mousedown', this.onBackgroundMousedownBound);
    window.removeEventListener('f11yStateUpdate', this.updateFocusableElmsBound);
    document.removeEventListener('scroll', this.checkBoundingBoxBound);
    window.removeEventListener('resize', this.checkBoundingBoxBound);
  }

  /**
   * Adds bound methods to the domNode elements
   */
  addNodeListeners() {
    if (!this.trigger) return;
    this.trigger.addEventListener('keydown', this.onTriggerKeydownBound);
    if (this.onTriggerClickBound) {
      this.trigger.addEventListener('click', this.onTriggerClickBound);
    }
    if (this.onTriggerHoverBound) {
      this.trigger.addEventListener('mouseover', this.onTriggerHoverBound);
    }
    this.domNode.addEventListener('focusin', this.onFocusInBound);
    this.domNode.addEventListener('focusout', this.onFocusOutBound);
  }

  /**
   * Adds bound methods to the domNode elements
   */
  removeNodeListeners() {
    if (!this.trigger) return;
    this.trigger.removeEventListener('keydown', this.onTriggerKeydownBound);
    if (this.onTriggerClickBound) {
      this.trigger.removeEventListener('click', this.onTriggerClickBound);
    }
    if (this.onTriggerHoverBound) {
      this.trigger.removeEventListener('mouseover', this.onTriggerHoverBound);
    }
    this.domNode.removeEventListener('focusin', this.onFocusInBound);
    this.domNode.removeEventListener('focusout', this.onFocusOutBound);
  }

  /**
   * Updates available focusable elements inside the dropdown
   */
  updateFocusableElms() {
    this.initDropdownItems();
  }

  /**
   * Checks whether the dropdown is open
   * @returns  {boolean}
   */
  get isOpen() {
    if (!this.trigger) return false;
    return this.trigger.getAttribute('aria-expanded') === "true";
  }

  /**
   * Sets focus to a menu item.
   * @param  {Node} targetItem New target menu item
   */
  setFocusToItem(targetItem) {
    if (!this.dropdownItems) return;
    this.dropdownItems.forEach(function (item) {
      if (item === targetItem) {
        item.tabIndex = 0;
        targetItem.focus();
      } else {
        item.tabIndex = -1;
      }
    });
  }

  /**
   * Sets focus to the first item in the dropdown menu
   */
  setFocusToFirstItem() {
    if (!this.firstItem) return;
    this.setFocusToItem(this.firstItem);
  }

  /**
   * Sets focus to the last item in the dropdown menu
   */
  setFocusToLastItem() {
    if (!this.lastItem) return;
    this.setFocusToItem(this.lastItem);
  }

  /**
   * Sets focus to the previous menu item
   * @param    {HTMLElement} currentItem The currently focused item within the dropdown menu
   * @returns  {Node} The newly focused item within the dropdown menu
   */
  #setFocusToPreviousItem(currentItem) {
    if (!this.dropdownItems || !this.firstItem || !this.lastItem) return currentItem;

    /** @type {Node} */
    let targetItem;
    let index;
    if (currentItem === this.firstItem) {
      targetItem = this.lastItem;
    } else {
      index = this.dropdownItems.indexOf(currentItem);
      targetItem = this.dropdownItems[index - 1];
    }
    this.setFocusToItem(targetItem);
    return targetItem;
  }

  /**
   * Sets focus to the next menu item
   * @param    {HTMLElement} currentItem Currently focused item within the dropdown menu
   * @returns  {Node} The newly focused item within the dropdown menu
   */
  #setFocusToNextItem(currentItem) {
    if (!this.dropdownItems || !this.firstItem || !this.lastItem) return currentItem;

    /** @type {Node} */
    let targetItem;
    let index;
    if (currentItem === this.lastItem) {
      targetItem = this.firstItem;
    } else {
      index = this.dropdownItems.indexOf(currentItem);
      targetItem = this.dropdownItems[index + 1];
    }
    this.setFocusToItem(targetItem);
    return targetItem;
  }

  /**
   * Sets focus by the the first character of a menu item
   * @param  {HTMLElement} currentItem Currently focused item within the dropdown menu
   * @param  {string} char The character to base the focus on
   */
  #setFocusByFirstChar(currentItem, char) {
    if (char.length > 1) return;
    if (!this.dropdownItems || !this.firstChars) return;
    char = char.toLowerCase();
    let start = this.dropdownItems.indexOf(currentItem) + 1;
    if (start >= this.dropdownItems.length) start = 0;
    let index = this.firstChars.indexOf(char, start);
    if (index === -1) index = this.firstChars.indexOf(char, 0);
    if (index > -1) this.setFocusToItem(this.dropdownItems[index]);
  }

  /**
   * Opens the dropdown
   * @param  {Event|KeyboardEvent|null} e The event that triggered this method
   */
  openDropdown() {
    let e = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    if (!this.trigger || !this.dropdown) return;
    this.domNode.dispatchEvent(this.#events.beforeOpen);
    this.options.onBeforeOpen(this, e);
    const domNode = this.domNode;
    const dropdown = this.dropdown;
    const trigger = this.trigger;
    const openClass = this.options.openClass;
    const animatingClass = window.f11y.settings.animatingClass;
    const animatingOpenClass = window.f11y.settings.animatingOpenClass;
    trigger.setAttribute('aria-expanded', 'true');
    dropdown.setAttribute('aria-hidden', 'false');
    domNode.classList.add(openClass);
    this.addGlobalListeners();
    if (this.options.awaitOpenAnimation) {
      let animationCount = 0;
      function animationEndHandler(e) {
        if (e.target === domNode || domNode.contains(e.target)) {
          if (dropdown.contains(e.target) && e.target !== dropdown) return;
          animationCount--;
          if (animationCount === 0) {
            domNode.classList.remove(animatingClass, animatingOpenClass);
            document.removeEventListener('animationend', animationEndHandler);
            document.removeEventListener('animationstart', animationStartHandler);
          }
        }
      }
      function animationStartHandler(e) {
        if (e.target === domNode || domNode.contains(e.target)) {
          if (dropdown.contains(e.target) && e.target !== dropdown) return;
          animationCount++;
        }
      }
      document.addEventListener('animationstart', animationStartHandler);
      document.addEventListener('animationend', animationEndHandler);
      domNode.classList.add(animatingClass, animatingOpenClass);
    }
    this.initDropdownItems();
    this.dispatchStateUpdateEvent();
    this.domNode.dispatchEvent(this.#events.afterOpen);
    this.options.onAfterOpen(this, e);
  }

  /**
   * Closes the dropdown
   * @param  {Event|KeyboardEvent|null} e The event that triggered this method
   */
  closeDropdown() {
    let e = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    if (!this.trigger || !this.dropdown) return;
    if (this.isOpen) {
      this.domNode.dispatchEvent(this.#events.beforeClose);
      this.options.onBeforeClose(this, e);
      const domNode = this.domNode;
      const dropdown = this.dropdown;
      const trigger = this.trigger;
      const openClass = this.options.openClass;
      const animatingClass = window.f11y.settings.animatingClass;
      const animatingCloseClass = window.f11y.settings.animatingCloseClass;
      dropdown.setAttribute('aria-hidden', 'true');
      if (this.options.awaitCloseAnimation) {
        let animationCount = 0;
        domNode.classList.add(animatingClass, animatingCloseClass);

        /**
         * Handle any when an animation ends within or on the this.domNode
         * @param {AnimationEvent} e AnimationEvent object
         * @returns 
         */
        function animationEndHandler(e) {
          if (e.target === domNode || domNode.contains(e.target)) {
            if (dropdown.contains(e.target) && e.target !== dropdown) return;
            animationCount--;
            if (animationCount === 0) {
              domNode.classList.remove(animatingClass, animatingCloseClass);
              domNode.classList.remove(openClass);
              document.removeEventListener('animationstart', animationStartHandler);
              document.removeEventListener('animationend', animationEndHandler);
            }
          }
        }

        /**
         * Handle any when an animation starts within or on the this.domNode
         * @param {AnimationEvent} e AnimationEvent object
         */
        function animationStartHandler(e) {
          if (e.target === domNode || domNode.contains(e.target)) {
            if (dropdown.contains(e.target) && e.target !== dropdown) return;
            animationCount++;
          }
        }
        document.addEventListener('animationstart', animationStartHandler);
        document.addEventListener('animationend', animationEndHandler);
      } else {
        domNode.classList.remove(openClass);
      }
      trigger.removeAttribute('aria-expanded');
      this.dispatchStateUpdateEvent();
      this.domNode.dispatchEvent(this.#events.afterClose);
      this.options.onAfterClose(this, e);
    }
    this.resetBoundingBox();
    this.removeGlobalListeners();
  }

  /**
   * Handles focus of Dropdown
   */
  #onFocusin() {
    this.domNode.classList.add('has-focus');
  }

  /**
   * Handles lose of focus on Dropdown
   */
  #onFocusout() {
    this.domNode.classList.remove('has-focus');
  }

  /**
   * Handles all Trigger toggle keyboard events
   * @param  {KeyboardEvent|null} e The event that triggered this method
   */
  #onTriggerKeydown() {
    let e = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    if (!e || !this.trigger) return;
    const key = e.key;
    let flag = false;
    switch (key) {
      case ' ':
      case 'Enter':
      case 'ArrowDown':
      case 'Down':
        this.openDropdown(e);
        this.checkBoundingBox();
        this.setFocusToFirstItem();
        flag = true;
        break;
      case 'Esc':
      case 'Escape':
        if (this.isOpen) {
          this.closeDropdown(e);
          this.trigger.focus();
          flag = true;
        } else {
          flag = false;
        }
        break;
      case 'Up':
      case 'ArrowUp':
        this.openDropdown(e);
        this.checkBoundingBox();
        this.setFocusToLastItem();
        flag = true;
        break;
      case 'Tab':
        if (this.isOpen) {
          this.closeDropdown(e);
        }
        flag = false;
        break;
    }
    if (flag) {
      e.stopPropagation();
      e.preventDefault();
    }
  }

  /**
   * Handles Trigger toggle click event
   * @param  {Event|KeyboardEvent|null} e The event that triggered this method
   */
  #onTriggerClick() {
    let e = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    if (!e || !this.trigger) return;
    if (this.isOpen) {
      this.closeDropdown(e);
      this.trigger.focus();
    } else {
      this.openDropdown(e);
      this.checkBoundingBox();
      this.setFocusToFirstItem();
    }
    e.stopPropagation();
    e.preventDefault();
  }

  /**
   * Handles Trigger toggle hover event
   * @param  {Event|KeyboardEvent|null} e The event that triggered this method
   */
  #onTriggerHover() {
    let e = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    if (!e || !this.trigger) return;
    if (!this.isOpen) {
      this.openDropdown(e);
      this.trigger;
      this.checkBoundingBox();
      this.setFocusToFirstItem();
      let timer;
      const closeDropdown = this.closeDropdown.bind(this);
      const domNode = this.domNode;
      function handleDomNodeMouseOver() {
        clearTimeout(timer);
      }
      function handleDomNodeMouseOut(e) {
        timer = setTimeout(() => {
          closeDropdown(e);
          domNode.removeEventListener('mouseout', handleDomNodeMouseOut);
          domNode.removeEventListener('mouseover', handleDomNodeMouseOver);
        }, 650);
      }
      domNode.addEventListener('mouseout', handleDomNodeMouseOut, false);
      domNode.addEventListener('mouseover', handleDomNodeMouseOver, false);
      e.stopPropagation();
      e.preventDefault();
    }
  }

  /**
   * Handles all keyboard events on the menu items
   * @param  {KeyboardEvent|null} e The event that triggered this method
   */
  #onItemKeydown() {
    let e = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    if (!e || !this.trigger || !e.currentTarget) return;
    const tgt = e.currentTarget;
    const key = e.key;
    let flag = false;

    /**
     * @param  {string} str
     */
    function isPrintableChar(str) {
      return str.length === 1 && str.match(/\S/);
    }
    function isInputElement(tgt) {
      return tgt.tagName === "INPUT" || tgt.tagName === "SELECT" || tgt.tagName === "TEXTAREA";
    }
    if (e.ctrlKey || e.altKey || e.metaKey) return;
    if (e.shiftKey) {
      if (isPrintableChar(key) && !isInputElement(tgt)) {
        this.#setFocusByFirstChar(tgt, key);
        flag = true;
      } else {
        flag = false;
      }
      if (e.key === 'Tab') {
        if (this.options.tabNavigation) {
          this.#setFocusToPreviousItem(tgt);
        } else {
          this.trigger.focus();
          this.closeDropdown(e);
        }
        flag = true;
      }
    } else {
      switch (key) {
        case ' ':
          if (tgt.href) {
            window.location.href = tgt.href;
          }
          break;
        case 'Esc':
        case 'Escape':
          this.closeDropdown(e);
          this.trigger.focus();
          flag = true;
          break;
        case 'Up':
        case 'ArrowUp':
          if (this.options.arrowNavigation) this.#setFocusToPreviousItem(tgt);
          flag = true;
          break;
        case 'ArrowDown':
        case 'Down':
          if (this.options.arrowNavigation) this.#setFocusToNextItem(tgt);
          flag = true;
          break;
        case 'Home':
        case 'PageUp':
          this.setFocusToFirstItem();
          flag = true;
          break;
        case 'End':
        case 'PageDown':
          this.setFocusToLastItem();
          flag = true;
          break;
        case 'Tab':
          if (this.options.tabNavigation) {
            this.#setFocusToNextItem(tgt);
          } else {
            this.closeDropdown(e);
            this.trigger.focus();
          }
          flag = true;
          break;
        case 'Enter':
          this.#onItemClick(e);
          break;
        default:
          if (isPrintableChar(key) && !isInputElement(tgt)) {
            this.#setFocusByFirstChar(tgt, key);
            flag = true;
          } else {
            flag = false;
          }
          break;
      }
    }
    if (flag) {
      e.stopPropagation();
      e.preventDefault();
    }
  }

  /**
   * Handles click events on menu items
   * @param  {Event|KeyboardEvent|null} e The event that triggered this method
   */
  #onItemClick() {
    let e = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    if (!e || !e.currentTarget || !this.updateTargetNode) return;
    if (this.options.updateOnSelect === true) {
      const itemTextContent = e.currentTarget.textContent;
      this.updateTargetNode.textContent = itemTextContent;
      this.updateTargetNode.value = itemTextContent;
    }
    if (this.options.closeOnSelect === true) {
      this.closeDropdown(e);
    }
  }

  /**
   * Checks the bounding box of the dropdown element against the window bounds
   */
  checkBoundingBox() {
    if (!this.dropdown) return;
    let dropdownBounds = this.dropdown.getBoundingClientRect();
    if (dropdownBounds.bottom > window.innerHeight) {
      this.domNode.setAttribute('f11y-out-of-bounds', 'bottom');
      return;
    }
    if (dropdownBounds.top < 0) {
      this.domNode.setAttribute('f11y-out-of-bounds', 'top');
      return;
    }
  }

  /**
   * Resets any class name changes by checkBoundingBox()
   */
  resetBoundingBox() {
    this.domNode.removeAttribute('f11y-out-of-bounds');
  }

  /**
   * Handles click events that are outside of the dropdown remit
   * @param  {Event|KeyboardEvent|null} e The event that triggered this method
   */
  #onBackgroundMousedown() {
    let e = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    if (!e || !this.trigger || !this.dropdown) return;
    if (!this.domNode.contains(e.target) && this.options.closeOnBackgroundClick) {
      if (this.isOpen) {
        this.closeDropdown(e);
        this.trigger.focus();
      }
    }
  }
}

class Layer {
  /**
   * @param {HTMLElement} domNode 
   * @param {LayerDefault} opts 
   */
  constructor(domNode, opts) {
    /** @type {LayerDefault} */
    const DEFAULTS = {
      onOpen: () => {},
      onClose: () => {},
      openTriggerAttribute: 'f11y-layer-open',
      closeTriggerAttribute: 'f11y-layer-close',
      openClass: window.f11y.settings.openClass,
      focusableElements: window.f11y.focusableElements,
      disableScroll: true,
      closeOnBackgroundClick: true,
      closeOnEscClick: true,
      awaitCloseAnimation: false,
      awaitOpenAnimation: false
    };

    /** @type {LayerDefault} */
    this.options = Object.assign(DEFAULTS, opts);

    /** @type {HTMLElement} */
    this.domNode = domNode;

    /** @type {string} */
    this.id = '';
    this.init();
  }

  /**
   * Initialises the class component
   */
  init() {
    this.id = this.domNode.id;
    this.dialog = this.domNode.querySelector('[role="dialog"]');
    this.triggerElms = document.querySelectorAll('[' + this.options.openTriggerAttribute + '="' + this.id + '"]');
    this.closeElms = document.querySelectorAll('[' + this.options.closeTriggerAttribute + '="' + this.id + '"]');

    /** @type {HTMLElement[]} */
    this.focusableElms = Array.from(this.domNode.querySelectorAll(this.options.focusableElements));
    if (!this.dialog) {
      console.warn('Dialog element is not defined in passsed domNode');
      return;
    }
    this.filterFocusableElms();
    this.openLayerBound = this.openLayer.bind(this);
    for (let i = 0; i < this.triggerElms.length; i++) {
      this.triggerElms[i].addEventListener('click', this.openLayerBound, true);
    }
    for (let i = 0; i < this.focusableElms.length; i++) {
      if (!this.firstElm) {
        this.firstElm = this.focusableElms[i];
      }
      this.lastElm = this.focusableElms[i];
    }
    this.onBackgroundMousedownBound = this.#onBackgroundMousedown.bind(this);
    this.onWindowKeydownBound = this.#onWindowKeydown.bind(this);
    this.domNode.classList.add(window.f11y.settings.initialisedClass);
  }

  /**
   * Refreshes the class component and calls init() and does any necessary resets
   */
  refresh() {
    if (!this.dialog) return;
    this.domNode.setAttribute('aria-hidden', 'true');
    this.dialog.setAttribute('aria-modal', 'true');
    this.dialog.setAttribute('role', 'dialog');
    this.init();
  }

  /**
   * Destroys the layer object
   */
  destroy() {
    if (!this.dialog || !this.triggerElms) return;
    this.closeLayer();
    this.domNode.removeAttribute('aria-hidden');
    this.dialog.removeAttribute('aria-modal');
    this.dialog.removeAttribute('role');
    this.domNode.classList.remove(window.f11y.settings.initialisedClass);
    for (let i = 0; i < this.triggerElms.length; i++) {
      this.triggerElms[i].removeEventListener('click', this.openLayerBound, true);
    }
  }

  /**
   * Removes (mutates) any [aria-hidden] elements from this.focusableElms array
   */
  filterFocusableElms() {
    if (!this.focusableElms) return;
    let i = this.focusableElms.length;
    while (i--) {
      const elm = this.focusableElms[i];
      const closestHidden = elm.closest('[aria-hidden]');
      if (closestHidden != this.domNode) {
        this.focusableElms.splice(i, 1);
      }
    }
  }

  /**
   * Checks whether the layer is open
   * @returns  {boolean}
   */
  isOpen() {
    return this.domNode.getAttribute('aria-hidden') === 'false';
  }

  /**
   * Checks whether the layer is open
   * @param {HTMLElement}  newElm Element Node to move focus to
   */
  setFocusToElm(newElm) {
    newElm.focus();
  }

  /**
   * Sets Focus to the first focusable item inside the layer
   */
  setFocusToFirstElm() {
    if (!this.firstElm) return;
    this.setFocusToElm(this.firstElm);
  }

  /**
   * Sets Focus to the last focusable item inside the layer
   */
  setFocusToLastElm() {
    if (!this.lastElm) return;
    this.setFocusToElm(this.lastElm);
  }

  /**
   * Sets Focus to the previous focusable item inside the layer
   * @param {HTMLElement}  currentElm the currently focused element within the layer
   * @returns  {HTMLElement} The newly focused element within the layer
   */
  #setFocusToPrevElm(currentElm) {
    if (!this.focusableElms || !this.firstElm || !this.lastElm) return currentElm;

    /** @type {HTMLElement} */
    let newElm;
    if (currentElm === this.firstElm) {
      newElm = this.lastElm;
    } else {
      let i = this.focusableElms.indexOf(currentElm);
      newElm = this.focusableElms[i - 1];
    }
    this.setFocusToElm(newElm);
    return newElm;
  }

  /**
   * Sets Focus to the next focusable item inside the layer
   * @param {HTMLElement}  currentElm the currently focused element within the layer
   * @returns  {HTMLElement} The newly focused element within the layer
   */
  #setFocusToNextElm(currentElm) {
    if (!this.focusableElms || !this.firstElm || !this.lastElm) return currentElm;

    /** @type {HTMLElement} */
    let newElm;
    if (currentElm === this.lastElm) {
      newElm = this.firstElm;
    } else {
      let i = this.focusableElms.indexOf(currentElm);
      newElm = this.focusableElms[i + 1];
    }
    this.setFocusToElm(newElm);
    return newElm;
  }

  /**
   * Opens the layer
   * @param {Event|KeyboardEvent|null}  e The event object that triggered the method
   */
  openLayer() {
    let e = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    this.activeElm = document.activeElement;
    const domNode = this.domNode;
    const dialog = this.dialog;
    const closeElms = this.closeElms;
    const docBody = document.querySelector('body');
    const openClass = this.options.openClass;
    const animatingClass = window.f11y.settings.animatingClass;
    const animatingOpenClass = window.f11y.settings.animatingOpenClass;
    const setFocus = () => this.setFocusToFirstElm();
    const addListeners = () => this.addGlobalListeners();
    const closeLayer = () => this.closeLayer();
    domNode.setAttribute('aria-hidden', 'false');
    domNode.classList.add(openClass);
    this.options.disableScroll ? docBody.style.setProperty('overflow', 'hidden') : '';
    if (this.options.awaitOpenAnimation) {
      let animationCount = 0;
      domNode.classList.add(animatingClass, animatingOpenClass);
      function animationEndHandler(e) {
        if (e.target === domNode || domNode.contains(e.target)) {
          if (dialog.contains(e.target) && e.target !== dialog) return;
          animationCount--;
          if (animationCount === 0) {
            domNode.classList.remove(animatingClass, animatingOpenClass);
            document.removeEventListener('animationend', animationEndHandler);
            document.removeEventListener('animationstart', animationStartHandler);
            for (let i = 0; i < closeElms.length; i++) {
              closeElms[i].addEventListener('click', closeLayer, true);
            }
            setFocus();
            addListeners();
          }
        }
      }
      function animationStartHandler(e) {
        if (e.target === domNode || domNode.contains(e.target)) {
          if (dialog.contains(e.target) && e.target !== dialog) return;
          animationCount++;
        }
      }
      document.addEventListener('animationstart', animationStartHandler);
      document.addEventListener('animationend', animationEndHandler);
    } else {
      for (let i = 0; i < closeElms.length; i++) {
        closeElms[i].addEventListener('click', closeLayer, true);
      }
      setFocus();
      addListeners();
    }
    window.f11y.store.activeLayer = this.id;
    window.f11y.store.activeLayers.push(this.id);
    window.dispatchEvent(window.f11y.events.stateUpdate);
    this.options.onOpen(this, e);
  }

  /**
   * Closes the layer
   * @param {Event | KeyboardEvent | null} e The event object that triggered the method
   */
  closeLayer() {
    let e = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    if (this.isOpen()) {
      if (this.id != window.f11y.store.activeLayer) return;
      const domNode = this.domNode;
      const dialog = this.dialog;
      const docBody = document.querySelector('body');
      const openClass = this.options.openClass;
      const animatingClass = window.f11y.settings.animatingClass;
      const animatingCloseClass = window.f11y.settings.animatingCloseClass;
      const removeListeners = () => this.removeGlobalListeners();
      let activeLayers = window.f11y.store.activeLayers;
      const i = activeLayers.indexOf(this.id);
      activeLayers.splice(i, 1);
      window.f11y.store.activeLayers = activeLayers;
      window.f11y.store.activeLayer = activeLayers[activeLayers.length - 1];
      domNode.setAttribute('aria-hidden', 'true');
      if (this.activeElm && this.activeElm.focus) this.activeElm.focus();
      if (this.options.awaitCloseAnimation) {
        let animationCount = 0;
        domNode.classList.add(animatingClass, animatingCloseClass);
        function animationEndHandler(e) {
          if (e.target === domNode || domNode.contains(e.target)) {
            if (dialog.contains(e.target) && e.target !== dialog) return;
            animationCount--;
            if (animationCount === 0) {
              domNode.classList.remove(animatingClass, animatingCloseClass);
              domNode.classList.remove(openClass);
              if (window.f11y.store.activeLayers.length <= 0) docBody.style.removeProperty('overflow');
              document.removeEventListener('animationstart', animationStartHandler);
              document.removeEventListener('animationend', animationEndHandler);
              removeListeners();
            }
          }
        }
        function animationStartHandler(e) {
          if (e.target === domNode || domNode.contains(e.target)) {
            if (dialog.contains(e.target) && e.target !== dialog) return;
            animationCount++;
          }
        }
        document.addEventListener('animationstart', animationStartHandler);
        document.addEventListener('animationend', animationEndHandler);
      } else {
        domNode.classList.remove(openClass);
        window.f11y.store.activeLayers.length <= 0 ? docBody.style.removeProperty('overflow') : '';
        removeListeners();
      }
      window.dispatchEvent(window.f11y.events.stateUpdate);
      this.options.onClose(this, e);
    }
  }

  /**
   * Adds bound method window events 
   */
  addGlobalListeners() {
    window.addEventListener('keydown', this.onWindowKeydownBound);
    if (this.options.closeOnBackgroundClick) window.addEventListener('mousedown', this.onBackgroundMousedownBound);
  }

  /**
   * Removes bound methods window events 
   */
  removeGlobalListeners() {
    window.removeEventListener('keydown', this.onWindowKeydownBound);
    if (this.options.closeOnBackgroundClick) window.removeEventListener('mousedown', this.onBackgroundMousedownBound);
  }

  /**
   * Closes a layer by id attribute
   * @param {string} targetLayer id of the layer to be closed
   */
  closeLayerById(targetLayer) {
    if (this.id === targetLayer) {
      this.closeLayer();
    }
  }

  /**
   * Implements window keydown events functionality
   * @param {KeyboardEvent} e The event object that triggered the method
   */
  #onWindowKeydown(e) {
    let flag = false;
    const tgt = e.target || e.currentTarget;
    if (e.shiftKey) {
      if (e.key === 'Tab') {
        this.#setFocusToPrevElm(tgt);
        flag = true;
      }
    } else {
      switch (e.key) {
        case 'Escape':
        case 'Esc':
          if (!this.options.closeOnEscClick) {
            flag = false;
          } else {
            this.closeLayer(e);
            flag = true;
          }
          break;
        case 'Tab':
          this.#setFocusToNextElm(tgt);
          flag = true;
          break;
        case 'Home':
        case 'PageUp':
          this.setFocusToFirstElm();
          flag = true;
          break;
        case 'End':
        case 'PageDown':
          this.setFocusToLastElm();
          flag = true;
          break;
      }
    }
    if (flag) {
      e.stopPropagation();
      e.preventDefault();
    }
  }

  /**
   * Handles click events that are outside of the layer dialog remit
   * @param  {Event} e The event that triggered this method
   */
  #onBackgroundMousedown(e) {
    if (!this.dialog.contains(e.target)) {
      if (this.isOpen()) {
        this.closeLayer(e);
        this.activeElm.focus();
      }
    }
  }
}

class Table {
  /**
   * @param {HTMLElement} domNode
   */
  constructor(domNode) {
    /** @type {HTMLElement} */
    this.domNode = domNode;
    this.init();
  }

  /**
   * Initialises the class component
   */
  init() {
    if (!this.domNode) return;
    this.tableId = this.domNode.getAttribute('id');
    this.tableGroups = Array.from(this.domNode.querySelectorAll('thead, tbody, tfoot'));
    this.tableRows = Array.from(this.domNode.querySelectorAll('tr'));
    this.tableHeaderCells = Array.from(this.domNode.querySelectorAll('th'));
    this.tableDataCells = Array.from(this.domNode.querySelectorAll('td'));
    this.styleElm = document.createElement("style");
    document.head.appendChild(this.styleElm);
    this.stylesheet = this.styleElm.sheet;
    this.insertCellHeaders();
    this.insertTableAria();
    this.domNode.classList.add(window.f11y.settings.initialisedClass);
  }

  /**
   * Refreshes the class component and calls init() and does any necessary resets
   */
  refresh() {
    this.init();
  }

  /**
   * Inserts Header td values as psuedo ::before content into stylesheet element
   */
  insertCellHeaders() {
    if (!this.tableHeaderCells || !this.stylesheet) return;
    for (let i = 0; i < this.tableHeaderCells.length; i += 1) {
      this.stylesheet.insertRule("#" + this.tableId + " td:nth-child(" + (i + 1) + ")::before {" + "content:'" + this.tableHeaderCells[i].innerText + "';" + "}", this.stylesheet.cssRules.length);
    }
  }

  /**
   * Inserts required aria into table for it to still be accessible if display style is change in order to make table repsonsive
   */
  insertTableAria() {
    if (!this.tableGroups || !this.tableRows || !this.tableDataCells || !this.tableHeaderCells) return;
    this.domNode.setAttribute('role', 'table');
    for (let i = 0; i < this.tableGroups.length; i++) {
      this.tableGroups[i].setAttribute('role', 'rowgroup');
    }
    for (let i = 0; i < this.tableRows.length; i++) {
      this.tableRows[i].setAttribute('role', 'row');
    }
    for (let i = 0; i < this.tableHeaderCells.length; i++) {
      this.tableHeaderCells[i].setAttribute('role', 'columnheader');
    }
    for (let i = 0; i < this.tableDataCells.length; i++) {
      this.tableDataCells[i].setAttribute('role', 'cell');
    }
  }
}

class TabList {
  /**
   * @param {HTMLElement} domNode 
   * @param {TabListDefault} opts 
   */
  constructor(domNode, opts) {
    /** @type {TabListDefault} */
    const DEFAULTS = {
      onChange: () => {},
      orientation: 'horizontal',
      disableActiveTab: false,
      changeOnNavigation: false,
      moveFocusOnSelect: false
    };

    /** @type {TabListDefault} */
    this.options = Object.assign(DEFAULTS, opts);

    /** @type {Element | HTMLElement} */
    this.domNode = domNode;
    this.init();
  }
  init() {
    this.tabs = Array.from(this.domNode.querySelectorAll('[role=tab]'));
    this.activeTabs = Array.from(this.domNode.querySelectorAll('[role=tab]:not([disabled])'));

    /** @type {Array<HTMLElement>} */
    this.tabpanels = [];
    for (let i = 0; i < this.tabs.length; i += 1) {
      const tab = this.tabs[i];
      const tabpanel = document.getElementById(tab.getAttribute('aria-controls'));
      tab.tabIndex = -1;
      if (!this.selected && tab.getAttribute('aria-selected') === 'true') {
        this.selected = tab;
      } else {
        tab.setAttribute('aria-selected', 'false');
      }
      this.tabpanels.push(tabpanel);
      tab.addEventListener('keydown', this.onKeydown.bind(this));
      tab.addEventListener('click', this.onClick.bind(this));
      if (!this.firstTab) {
        this.firstTab = tab;
      }
      this.lastTab = tab;
    }
    this.selected ? this.setSelectedTab(this.selected) : this.setSelectedTab(this.firstTab);
    this.dispatchStateUpdateEvent();
    this.domNode.classList.add(window.f11y.settings.initialisedClass);
  }
  dispatchStateUpdateEvent() {
    window.dispatchEvent(window.f11y.events.stateUpdate);
  }

  /**
   * Refreshes the class component and calls init() and does any necessary resets
   */
  refresh() {
    this.init();
  }

  /**
   * Handles changing a tab
   * @param {HTMLElement} targetTab 
   * @param {Event | KeyboardEvent | null} event 
   */
  handleTabChange(targetTab) {
    let event = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    this.setSelectedTab(targetTab);
    this.dispatchStateUpdateEvent();
    this.options.onChange(this, event);
  }

  /**
   * 
   */
  setActiveTabs() {
    this.activeTabs = Array.from(this.domNode.querySelectorAll('[role=tab]:not([disabled])'));
    this.firstActiveTab = null;
    for (let i = 0; i < this.activeTabs.length; i += 1) {
      const tab = this.activeTabs[i];
      if (!this.firstActiveTab) this.firstActiveTab = tab;
      this.lastActiveTab = tab;
    }
    this.nextTab = this.getNextTab();
    this.prevTab = this.getPrevTab();
  }

  /**
   * Sets the passed tab and corresponding panel to be selected
   * @param {Element} targetTab 
   */
  setSelectedTab(targetTab) {
    for (let i = 0; i < this.tabs.length; i += 1) {
      const tab = this.tabs[i];
      if (targetTab.id === tab.id) {
        this.selected = this.tabs[i];
        tab.setAttribute('aria-selected', 'true');
        tab.removeAttribute('tabindex');
        this.tabpanels[i].removeAttribute('hidden');
        if (this.options.disableActiveTab === true) {
          tab.setAttribute('disabled', '');
          tab.tabIndex = -1;
        }
      } else {
        tab.setAttribute('aria-selected', 'false');
        tab.removeAttribute('disabled');
        tab.tabIndex = -1;
        this.tabpanels[i].setAttribute('hidden', '');
      }
    }
    this.setActiveTabs();
  }
  getNextTab() {
    let tab = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    let nextTab;
    let tabList;
    const currentTab = tab ?? this.selected;
    if (this.options.disableActiveTab) {
      tabList = this.activeTabs;
    } else {
      tabList = this.tabs;
    }
    const i = tabList.indexOf(currentTab);
    if (currentTab.id === this.lastActiveTab.id) {
      nextTab = this.firstActiveTab;
    } else {
      nextTab = tabList[i + 1];
    }
    return nextTab;
  }
  getPrevTab() {
    let tab = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    let prevTab;
    let tabList;
    const currentTab = tab ?? this.selected;
    if (this.options.disableActiveTab) {
      tabList = this.activeTabs;
    } else {
      tabList = this.tabs;
    }
    const i = tabList.indexOf(currentTab);
    if (currentTab.id === this.firstActiveTab.id) {
      prevTab = this.lastActiveTab;
    } else {
      prevTab = tabList[i - 1];
    }
    return prevTab;
  }

  /**
   * Moves focus to passed tab
   * @param {Element} targetTab 
   */
  moveFocusToTab(targetTab) {
    targetTab.focus();
  }

  /**
   * Handles keydown events on tabs
   * @param {KeyboardEvent} event 
   */
  onKeydown(event) {
    const tgt = event.currentTarget;
    const change = this.options.changeOnNavigation;
    const orientation = this.options.orientation;
    let flag = false;
    switch (event.key) {
      case 'ArrowLeft':
        if (orientation === 'horizontal') {
          if (change) {
            this.handleTabChange(this.prevTab);
            this.moveFocusToTab(this.selected);
          } else {
            this.moveFocusToTab(this.getPrevTab(tgt));
          }
          flag = true;
        }
        break;
      case 'ArrowRight':
        if (orientation === 'horizontal') {
          if (change) {
            this.handleTabChange(this.nextTab);
            this.moveFocusToTab(this.selected);
          } else {
            this.moveFocusToTab(this.getNextTab(tgt));
          }
          flag = true;
        }
        break;
      case 'ArrowUp':
        if (orientation === 'vertical') {
          if (change) {
            this.handleTabChange(this.prevTab);
            this.moveFocusToTab(this.selected);
          } else {
            this.moveFocusToTab(this.getPrevTab(tgt));
          }
          flag = true;
        }
        break;
      case 'ArrowDown':
        if (orientation === 'vertical') {
          if (change) {
            this.handleTabChange(this.nextTab);
            this.moveFocusToTab(this.selected);
          } else {
            this.moveFocusToTab(this.getNextTab(tgt));
          }
          flag = true;
        }
        break;
      case 'Home':
        if (change) {
          this.handleTabChange(this.firstActiveTab);
          this.moveFocusToTab(this.selected);
        } else {
          this.moveFocusToTab(this.firstActiveTab);
        }
        flag = true;
        break;
      case 'End':
        if (change) {
          this.handleTabChange(this.lastActiveTab);
          this.moveFocusToTab(this.selected);
        } else {
          this.moveFocusToTab(this.lastActiveTab);
        }
        flag = true;
        break;
    }
    if (flag) {
      event.stopPropagation();
      event.preventDefault();
    }
  }

  /**
   * Handles click events on tabs
   * @param {Event} event 
   */
  onClick(event) {
    this.handleTabChange(event.currentTarget, event);
    if (this.options.moveFocusOnChange === true) {
      this.moveFocusToTab(this.nextTab);
    }
  }
}

class Tooltip {
  /**
   * @param {HTMLElement} domNode 
   * @param {TooltipDefault} opts 
   */
  constructor(domNode, opts) {
    /** @type {TooltipDefault} */
    const DEFAULTS = {
      onOpen: () => {},
      onClose: () => {},
      positionAttributeName: 'f11y-tooltip-position',
      openClass: window.f11y.settings.openClass,
      leewayTiming: 750,
      delayTiming: 2000,
      awaitCloseAnimation: false,
      awaitOpenAnimation: false
    };

    /** @type {TooltipDefault} */
    this.options = Object.assign(DEFAULTS, opts);
    this.domNode = domNode;
    this.init();
  }

  /**
   * Initialises the class component
   */
  init() {
    this.leeway = 0;
    this.delay = 0;
    this.trigger = this.domNode.querySelector('[aria-labelledby]');
    this.tooltip = this.domNode.querySelector('[role=tooltip]');
    this.tooltipPos = this.getTooltipPos();
    this.onTooltipKeydownBound = this.#onTooltipKeydown.bind(this);
    this.onBackgroundMousedownBound = this.#onBackgroundMousedown.bind(this);
    this.domNode.addEventListener('mouseenter', this.openTooltip.bind(this));
    this.trigger.addEventListener('touchstart', this.openTooltip.bind(this));
    this.domNode.addEventListener('focusin', this.onFocusin.bind(this));
    this.trigger.addEventListener('focusin', this.openTooltip.bind(this));
    this.domNode.addEventListener('mouseleave', this.closeTooltip.bind(this));
    this.domNode.addEventListener('touchend', this.closeTooltip.bind(this));
    this.domNode.addEventListener('focusout', this.onFocusout.bind(this));
    this.trigger.addEventListener('focusout', this.closeTooltip.bind(this));
    document.addEventListener('scroll', this.checkBoundingBox.bind(this));
    window.addEventListener('resize', this.checkBoundingBox.bind(this));
    this.domNode.classList.add(window.f11y.settings.initialisedClass);
  }

  /**
   * Refreshes the class component and calls init() and does any necessary resets
   */
  refresh() {
    this.init();
  }

  /**
   * Checks whether the layer is open
   * @returns  {boolean}
   */
  isOpen() {
    return this.domNode.classList.contains(this.options.openClass) === true;
  }

  /**
   * Opens the tooltip
   */
  openTooltip() {
    const domNode = this.domNode;
    const tooltip = this.tooltip;
    const openClass = this.options.openClass;
    const animatingClass = window.f11y.settings.animatingClass;
    const animatingOpenClass = window.f11y.settings.animatingOpenClass;
    this.options.delayTiming;
    const leeway = this.leeway;
    if (!this.isOpen() && this.options.awaitOpenAnimation) {
      domNode.addEventListener('animationend', handler);
      domNode.classList.add(animatingClass, animatingOpenClass);
      function handler() {
        domNode.classList.remove(animatingClass, animatingOpenClass);
        domNode.classList.add(openClass);
        tooltip.classList.add(openClass);
        clearTimeout(leeway);
        domNode.removeEventListener('animationend', handler);
      }
    } else {
      domNode.classList.add(openClass);
      tooltip.classList.add(openClass);
      clearTimeout(leeway);
    }
    this.checkBoundingBox();
    this.addGlobalListeners();
    window.dispatchEvent(window.f11y.events.stateUpdate);
  }

  /**
   * Closes the tooltip
   */
  closeTooltip() {
    if (this.isOpen()) {
      const domNode = this.domNode;
      const tooltip = this.tooltip;
      const openClass = this.options.openClass;
      const leeway = this.options.leewayTiming;
      const animatingClass = window.f11y.settings.animatingClass;
      const animatingCloseClass = window.f11y.settings.animatingCloseClass;
      if (this.options.awaitCloseAnimation) {
        this.leeway = setTimeout(function () {
          domNode.addEventListener('animationend', handler);
          domNode.classList.add(animatingClass, animatingCloseClass);
          function handler() {
            domNode.classList.remove(animatingClass, animatingCloseClass);
            domNode.classList.remove(openClass);
            tooltip.classList.remove(openClass);
            domNode.removeEventListener('animationend', handler);
          }
        }, leeway);
      } else {
        this.leeway = setTimeout(function () {
          domNode.classList.remove(openClass);
          tooltip.classList.remove(openClass);
        }, leeway);
      }
      window.dispatchEvent(window.f11y.events.stateUpdate);
    }
    this.resetBoundingBox();
    this.removeGlobalListeners();
  }

  /**
   * Registers global event listeners
   */
  addGlobalListeners() {
    document.addEventListener('keydown', this.onTooltipKeydownBound);
    window.addEventListener('mousedown', this.onBackgroundMousedownBound, true);
  }

  /**
   * Removes global event listeners
   */
  removeGlobalListeners() {
    document.removeEventListener('keydown', this.onTooltipKeydownBound);
    window.removeEventListener('mousedown', this.onBackgroundMousedownBound, true);
  }

  /**
   * Handles the domNode receiving focus
   */
  onFocusin() {
    this.domNode.classList.add('has-focus');
  }

  /**
   * Handles the domNode losing focus
   */
  onFocusout() {
    this.domNode.classList.remove('has-focus');
  }

  /**
   * Handles a keydown event on the tooltip target
   * @param  {KeyboardEvent} e The event object that triggered the method
   * @returns {void}
   */
  #onTooltipKeydown(e) {
    const key = e.key;
    let flag = false;
    switch (key) {
      case 'Escape':
      case 'Esc':
        this.closeTooltip();
        flag = true;
        break;
    }
    if (flag) {
      e.stopPropagation();
      e.preventDefault();
    }
  }

  /**
   * Handles a mouse click event outside the domNode
   * @param  {Event} e The event object that triggered the method
   */
  #onBackgroundMousedown(e) {
    if (!this.domNode.contains(e.target)) {
      if (this.isOpen()) {
        this.closeTooltip();
      }
    }
  }

  /**
   * Returns the position value from the attribute or a default if one is not set
   * @return  {string} the position value
   */
  getTooltipPos() {
    let setting = 'bottom';
    if (this.domNode.getAttribute(this.options.positionAttributeName)) {
      setting = this.domNode.getAttribute(this.options.positionAttributeName);
    }
    return setting;
  }

  /** 
   * Checks the bounding box of the tooltip element against the window bounds
   */
  checkBoundingBox() {
    let bounds = this.tooltip.getBoundingClientRect();
    this.checkHorizontalBounding(bounds);
    this.checkVerticalBounding(bounds);
  }

  /**
   * Checks the bounds of the tooltip against the window and sets offset css var
   * @param {DOMRect} bounds the bounding box values of the tooltip
   */
  checkHorizontalBounding(bounds) {
    let windowWidth = window.innerWidth;
    let translateAmount;
    if (bounds.right > windowWidth && bounds.left < 0) {
      console.warn('Tooltip width too wide for the window');
      return;
    }
    if (bounds.right > windowWidth) {
      //move it left
      translateAmount = windowWidth - Math.round(bounds.right) - Math.round(bounds.width) / 1.6;
      this.domNode.style.setProperty('--f11y-tooltip-offset', translateAmount);
    }
    if (bounds.left < 0) {
      // move it right
      translateAmount = Math.floor(bounds.width / 2);
      this.domNode.style.setProperty('--f11y-tooltip-offset', translateAmount);
    }
  }

  /**
   * Checks the bounds of the tooltip against the window and change the position attribute
   * @param {DOMRect} bounds the bounding box values of the tooltip
   */
  checkVerticalBounding(bounds) {
    let windowHeight = window.innerHeight;
    if (bounds.bottom > windowHeight && bounds.top < 0) {
      console.warn('Tooltip height too high for the window');
      return;
    }
    if (bounds.bottom > windowHeight) {
      // move it up
      this.domNode.setAttribute(this.options.positionAttributeName, 'top');
      return;
    }
    if (bounds.top < 0) {
      //move it down
      this.domNode.setAttribute(this.options.positionAttributeName, 'bottom');
      return;
    }
  }

  /**
   * Resets any changes made by checkBoundingBox()
   */
  resetBoundingBox() {
    this.domNode.style.removeProperty('--f11y-tooltip-offset');
    this.domNode.setAttribute(this.options.positionAttributeName, this.tooltipPos);
  }
}

/**
 * Checks if something is set aka not null or undefined
 * @param {*} thing What do you want to check exists?
 * @returns {boolean}
 */
function isSet(thing) {
  return typeof thing !== 'undefined' && thing !== null;
}

/**
 * @module f11y
 */

/**
 * Represents the f11y module.
 * @namespace f11y
 */
const createF11y = () => ({
  components: {
    Accordion,
    Dropdown,
    Layer,
    Table,
    TabList,
    Tooltip
  },
  focusableElements: ['a[href]:not([disabled]):not([hidden]):not([aria-hidden])', 'area[href]:not([disabled]):not([hidden]):not([aria-hidden])', 'input:not([disabled]):not([type="hidden"]):not([aria-hidden])', 'select:not([disabled]):not([aria-hidden])', 'textarea:not([disabled]):not([aria-hidden])', 'button:not([disabled]):not([aria-hidden])', 'iframe:not([disabled]):not([hidden]):not([aria-hidden])', 'object:not([disabled]):not([hidden]):not([aria-hidden])', 'embed:not([disabled]):not([hidden]):not([aria-hidden])', '[contenteditable]:not([disabled]):not([hidden]):not([aria-hidden])', '[tabindex]'],
  settings: {
    initialisedClass: 'has-initialised',
    openClass: 'is-open',
    animatingClass: 'is-animating',
    animatingOpenClass: 'is-opening',
    animatingCloseClass: 'is-closing',
    awaitOpenAnimation: false,
    awaitCloseAnimation: false
  },
  store: {
    activeLayers: [],
    activeLayer: ''
  },
  utils: {
    isSet: isSet,
    isFocusable: isFocusable,
    getFocusable: getFocusable
  },
  events: {
    stateUpdate: new Event('f11ystateupdate')
  }
});

// Create a default instance of f11y
const defaultF11y = createF11y();

/**
 * Initializes the f11y module and attaches it to the window object.
 */
const initialiseF11y = () => {
  if (typeof window !== 'undefined') {
    window.f11y = defaultF11y;
  }
};
initialiseF11y();

export { createF11y, defaultF11y as default };
